import {
  SessionProps,
  SessionStatus,
  SessionCurrency,
  BookingQuestion,
  AvailabilitySchedule,
  SessionCoverImage,
} from "./session.types";

/**
 * Session entity.
 * Encapsulates session data and business rules.
 *
 * Pure TypeScript - no React imports.
 */
export class Session {
  private readonly props: SessionProps;

  private constructor(props: SessionProps) {
    this.props = props;
  }

  /**
   * Creates a Session entity with validation.
   * @throws Error if required fields are missing or invalid
   */
  static create(props: SessionProps): Session {
    if (props.id === undefined || props.id === null) {
      throw new Error("Session id is required");
    }

    if (!props.name || !props.name.trim()) {
      throw new Error("Session name is required");
    }

    if (props.callDuration <= 0) {
      throw new Error("Call duration must be positive");
    }

    if (props.callPrice < 0) {
      throw new Error("Call price cannot be negative");
    }

    return new Session(props);
  }

  // ==================== Getters ====================

  get id(): number {
    return this.props.id;
  }

  get userId(): number {
    return this.props.userId;
  }

  get name(): string {
    return this.props.name;
  }

  get slug(): string {
    return this.props.slug;
  }

  get description(): string {
    return this.props.description;
  }

  get statusId(): number {
    return this.props.statusId;
  }

  get statusName(): SessionStatus {
    return this.props.statusName;
  }

  get callDuration(): number {
    return this.props.callDuration;
  }

  get callPrice(): number {
    return this.props.callPrice;
  }

  get callPriceCurrency(): string {
    return this.props.callPriceCurrency;
  }

  get currencies(): SessionCurrency[] {
    return this.props.currencies;
  }

  get bookingQuestions(): BookingQuestion[] {
    return this.props.bookingQuestions;
  }

  get requestPhoneNumber(): boolean {
    return this.props.requestPhoneNumber;
  }

  get hideServiceFromMenu(): boolean {
    return this.props.hideServiceFromMenu;
  }

  get videoLocation(): boolean {
    return this.props.videoLocation;
  }

  get audioLocation(): boolean {
    return this.props.audioLocation;
  }

  get videoPlatform(): string {
    return this.props.videoPlatform ?? "";
  }

  get urlRedirect(): string {
    return this.props.urlRedirect;
  }

  get tinyUrl(): string {
    return this.props.tinyUrl;
  }

  get coverPicture(): string {
    return this.props.coverPicture;
  }

  get coverImages(): SessionCoverImage[] {
    return this.props.coverImages;
  }

  get enableCurrencyConversion(): boolean {
    return this.props.enableCurrencyConversion;
  }

  get availabilitySchedule(): AvailabilitySchedule | null {
    return this.props.availabilitySchedule;
  }

  get createdAt(): string {
    return this.props.createdAt;
  }

  get updatedAt(): string {
    return this.props.updatedAt;
  }

  get isBundle(): boolean {
    return this.props.isBundle;
  }

  get numberOfSessions(): number | null {
    return this.props.numberOfSessions;
  }

  get bookingInterval(): string | null {
    return this.props.bookingInterval;
  }

  get hasGoogleMeet(): boolean | undefined {
    return this.props.hasGoogleMeet;
  }

  // ==================== Business Methods ====================

  /**
   * Check if the session is currently active.
   */
  isActive(): boolean {
    return this.props.statusName === "Active";
  }

  /**
   * Check if the session is free (no charge).
   */
  isFree(): boolean {
    return this.props.callPrice === 0;
  }

  /**
   * Get formatted display price string.
   * Returns "Free" for free sessions, otherwise "{currency} {price}".
   */
  getDisplayPrice(): string {
    if (this.isFree()) {
      return "Free";
    }
    return `${this.props.callPriceCurrency} ${this.props.callPrice}`;
  }

  /**
   * Returns the raw properties for serialization or mapping.
   */
  toProps(): SessionProps {
    return {...this.props};
  }
}
